import React from 'react';
import {normalizeThickness, formatThickness} from './moulding-filters';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {MouldingCustomization} from '../../i';
import * as emitter from '../../../global/emitter';
import AbTestStore, {ABTestKeys} from '../../../global/store/ab-test-store';
import ModalSimple from '../../../global/modal-simple';
import InfoIcon from '../shared/info-icon';
import {MouldingsList} from './components/mouldings-list';
import {screenResizeStore} from '../../../global/store/screen-resize-store';
import {Portal} from '../../../util';
import {selectMoulding} from '../../selection/moulding';
import {disclosurePanelClass} from '../disclosure/panel-class';

@observer
export default class Moulding extends React.Component {
    @observable collapsed: boolean = true;
    @observable collapsed_number: number = 6;
    @observable panelOpen: boolean = false;
    @observable finishes: string[] = [];
    @observable selected_finish: string = '-1';
    @observable selected_color: string = '-1';
    @observable selected_thickness: string = '-1';
    @observable show_names: boolean = false;
    private collapsedBeforeFilters = true;

    constructor(props) {
        super(props);
        this.collapseToggle = this.collapseToggle.bind(this);
        this.save = this.save.bind(this);
        this.changeColor = this.changeColor.bind(this);
        makeObservable(this);
    }

    abForceUpdate = async () => {
        this.forceUpdate();

        if (s.prod.IsCAF) {
            if (AbTestStore.ABIsVariant(ABTestKeys.caf_single, 1)) {
                this.save(141);
            } else if (AbTestStore.ABIsVariant(ABTestKeys.caf_single, 2)) {
                this.save(23376);
            }
        } else if (s.prod.MouldingID && s.prod.MouldingID > 0) {
            this.save(s.prod.MouldingID);
        }
    };

    componentDidMount(): void {
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', this.abForceUpdate);

        // console.log(s.prod.mouldings);
        const _all = s.prod.mouldings.filter(x => x.fpc_name && x.fpc_name !== '').map(x => x.fpc_name);
        this.finishes = [...new Set(_all)].sort();
        // console.log(this.finishes);

        if (s.prod.IsCAF) {
            if (AbTestStore.ABIsVariant(ABTestKeys.caf_single, 1)) {
                this.save(141);
            } else if (AbTestStore.ABIsVariant(ABTestKeys.caf_single, 2)) {
                this.save(23376);
            }
        }
    }

    componentWillUnmount() {
        emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
    }

    @action
    showNames = () => {
        this.show_names = true;
    };

    @action
    collapseToggle = async () => {
        this.collapsed = !this.collapsed;
        this.collapsed_number = this.collapsed ? 6 : 1000;
    };

    @action
    save = async (id: number) => {
        selectMoulding(id);
    };

    @action
    changeDesktopFilter = (filter: 'color' | 'thickness', value: string) => {
        const hadFilter = this.selected_color !== '-1' || this.selected_thickness !== '-1';
        if (filter === 'color') this.selected_color = value;
        else this.selected_thickness = value;
        const hasFilter = this.selected_color !== '-1' || this.selected_thickness !== '-1';
        if (hasFilter && !hadFilter) this.collapsedBeforeFilters = this.collapsed;
        this.collapsed = hasFilter ? false : this.collapsedBeforeFilters;
        this.collapsed_number = this.collapsed ? 6 : 1000;
    };

    @action
    changeColor = async e => {
        this.selected_finish = e.target.value as string;
        if (this.selected_finish === '-1') {
            this.collapsed_number = this.collapsed ? 6 : 1000;
        } else {
            this.collapsed_number = 1000;
            if (this.collapsed) {
                this.collapseToggle();
            }
        }
    };
    @action
    togglePanel = () => {
        if (!this.canCollapse) return;
        this.panelOpen = !this.panelOpen;
    };

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || this.isMobile;
    }

    @computed get isMobile(): boolean {
        return screenResizeStore.width < 960;
    }

    render() {
        if (!s.ready || !s.ui.show_cust || !s.prod.mouldings) {
            return;
        }

        const isDesktopCaf = s.prod.IsCAF && screenResizeStore.width >= 1200;
        const colors = [...new Set(s.prod.mouldings.map(m => m.MouldingFinish).filter(color => color && color.trim() !== ''))].sort((a, b) => a.localeCompare(b));
        const thicknesses = [...new Set(s.prod.mouldings.map(m => normalizeThickness(m.thickness)).filter((value): value is number => value !== null))].sort((a, b) => a - b);
        const desktopFilters = (
            <div style={{display: 'flex', gap: 16}}>
                <div
                    className="cust-lino-select"
                    style={{width: 160, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4}}
                >
                    <label htmlFor="moulding-color">Color:</label>
                    <select
                        id="moulding-color"
                        className="form-select"
                        aria-label="Select moulding color"
                        style={{width: 160}}
                        value={this.selected_color}
                        onChange={e => this.changeDesktopFilter('color', e.target.value)}
                    >
                        <option value="-1">All</option>
                        {colors.map(color => (
                            <option
                                key={color}
                                value={color}
                            >
                                {color}
                            </option>
                        ))}
                    </select>
                </div>
                <div
                    className="cust-lino-select"
                    style={{width: 160, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4}}
                >
                    <label htmlFor="moulding-thickness">Thickness:</label>
                    <select
                        id="moulding-thickness"
                        className="form-select"
                        aria-label="Select moulding thickness"
                        style={{width: 160}}
                        value={this.selected_thickness}
                        onChange={e => this.changeDesktopFilter('thickness', e.target.value)}
                    >
                        <option value="-1">All</option>
                        {thicknesses.map(thickness => (
                            <option
                                key={thickness}
                                value={String(thickness)}
                            >
                                {formatThickness(thickness)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );

        return (
            <div
                className="gray-box"
                style={{position: 'relative', padding: 0}}
            >
                <div>
                    <div>
                        <div
                            style={{
                                padding: '0 12px',
                                position: 'relative',
                            }}
                        >
                            <div
                                role={this.canCollapse ? 'button' : undefined}
                                tabIndex={this.canCollapse ? 0 : undefined}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.currentTarget.click();
                                    }
                                }}
                                className="d-none d-xl-block toggle-bs"
                                onClick={this.togglePanel}
                                aria-expanded={this.canCollapse ? this.panelOpen : undefined}
                                aria-controls={this.canCollapse ? 'moulding-inner' : undefined}
                            >
                                <div
                                    className="cust-desktop-head"
                                    style={{paddingBottom: s.prod.IsCAF ? 8 : 0}}
                                >
                                    <span>Frame Moulding: </span>
                                    <span>{s.ui.title_moulding}</span>
                                </div>
                            </div>

                            <div
                                role={this.canCollapse ? 'button' : undefined}
                                tabIndex={this.canCollapse ? 0 : undefined}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.currentTarget.click();
                                    }
                                }}
                                className="d-block d-xl-none toggle-bs"
                                onClick={this.togglePanel}
                                aria-expanded={this.canCollapse ? this.panelOpen : undefined}
                                aria-controls={this.canCollapse ? 'moulding-inner' : undefined}
                            >
                                <div className="cust-flex-head">
                                    <div>
                                        <div
                                            className="header-std flexer cust-flex2"
                                            style={{paddingRight: '0.25rem'}}
                                        >
                                            Frame Moulding
                                        </div>
                                        <div className="cust-flex2">{s.ui.title_moulding}</div>
                                    </div>
                                    <div>
                                        {this.canCollapse &&
                                            this.collapsed &&
                                            s.prod.mouldings
                                                .filter(m => m.id === s.ps.moulding_id)
                                                .map((m: MouldingCustomization, i: number) => (
                                                    <img
                                                        key={i}
                                                        src={`https://${Portal?.CDNDomain}/images/entities/moulding/${m.name.toLowerCase()}_thumb_90.png`}
                                                        alt={m.title}
                                                    />
                                                ))}
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: this.isMobile ? 6 : s.ps.is_caf ? 10 : 5,
                                    left: this.isMobile ? 140 : 'auto',
                                    right: s.ps.is_caf ? 40 : !this.isMobile ? 8 : 'auto',
                                }}
                            >
                                <InfoIcon
                                    id="modal-moulding"
                                    ariaLabel="Open dialog with moulding information for this product"
                                    useMargin={true}
                                    styleBlack={true}
                                    onClick={this.showNames}
                                />
                            </div>
                        </div>

                        <div
                            id="moulding-inner"
                            className={disclosurePanelClass(this.canCollapse, this.panelOpen)}
                            style={{padding: '0 12px'}}
                        >
                            {s.prod && s.prod.IsCAF && (
                                <div>
                                    {isDesktopCaf ? (
                                        desktopFilters
                                    ) : (
                                        <div
                                            className="cust-lino-select"
                                            style={{width: '100%'}}
                                        >
                                            <label htmlFor="moulding-finish">Finish Color:</label>
                                            <select
                                                id="moulding-finish"
                                                className="form-select"
                                                aria-label="Select finish color"
                                                onChange={this.changeColor}
                                                style={{width: 160, marginLeft: 8}}
                                            >
                                                <option value="-1">View All</option>
                                                {this.finishes.map(x => (
                                                    <option
                                                        key={x}
                                                        value={x}
                                                    >
                                                        {x}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <hr style={{margin: '0.5rem 0'}} />
                                </div>
                            )}

                            <MouldingsList
                                collapsed={this.collapsed}
                                collapsedNumber={this.collapsed_number}
                                selectedFinish={this.selected_finish}
                                selectedColor={this.selected_color}
                                selectedThickness={this.selected_thickness}
                                save={this.save}
                                showNames={this.show_names}
                            />

                            {s.prod.mouldings.length > 6 && (
                                <button
                                    className="btn btn-outline-secondary btn-sm btn-toggle"
                                    onClick={this.collapseToggle}
                                    style={{margin: '4px 0 2px 0', color: '#000000', display: 'flex', justifySelf: 'center'}}
                                    aria-label={this.collapsed ? `+ View all ${s.prod.mouldings.length} mouldings` : '- View less mouldings'}
                                >
                                    {this.collapsed ? `+ View all ${s.prod.mouldings.length} mouldings` : '- View less mouldings'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <ModalSimple
                    id="modal-moulding"
                    title="Moulding Information"
                >
                    <p>
                        A wood frame is a handcrafted border made from high-quality wood mouldings,designed to beautifully protect and showcase your diploma, certificate, or special keepsake. Our frames come in a variety of classic and modern styles, with options ranging from sleek, minimal profiles
                        to elegant, deep shadowbox designs. Each frame is available in different finishes and colors, ensuring a perfect match for your decor or personal taste. Whether you choose a timeless traditional look or a contemporary design, our wood frames offer durability, sophistication,
                        and exceptional value, all tailored to highlight your achievement.
                    </p>
                </ModalSimple>
            </div>
        );
    }
}
